import { BankAccountDetails, PaymentType } from '@afterpay/types'
import { EnrichedBankAccountDetails, PaymentMethodType, BankAccountErrorReason } from '../model'
import { PreferredCard } from './credit-card'

export const findBankAccount = (savedBankAccounts: BankAccountDetails[]) => (id: number) => {
  return savedBankAccounts.find((account) => account.id === id)
}

export const enrichBankAccount = (account: BankAccountDetails, preferredCard?: PreferredCard): EnrichedBankAccountDetails => ({
  paymentMethodType: PaymentMethodType.ACH_BANK_ACCOUNT,
  // Can not use bank account when the PreferredCard is inValid
  error: typeof preferredCard !== 'undefined' && !preferredCard.valid ? 'noValidCard' : null,
  ...account,
})

export const enrichBankAccountOverdue = (account: BankAccountDetails): EnrichedBankAccountDetails => ({
  paymentMethodType: PaymentMethodType.ACH_BANK_ACCOUNT,
  // Can not use bank account for overdue payments
  error: 'checkingNotAllowed',
  ...account,
})

export const enrichBankAccountByPaymentType = (account: BankAccountDetails, paymentType: PaymentType): EnrichedBankAccountDetails => ({
  paymentMethodType: PaymentMethodType.ACH_BANK_ACCOUNT,
  // Can not use bank account for PCL payment type
  error: paymentType === PaymentType.PCL ? 'checkingNotAllowed' : null,
  ...account,
})

export interface PreferredBankAccount {
  account: EnrichedBankAccountDetails | null
  valid: boolean
  switched: boolean
  reason: BankAccountErrorReason | null // when the switched is true we need a reason of why is switched
}

export const selectPreferredBankAccount = (
  preferredBankAccount: EnrichedBankAccountDetails | null,
  savedBankAccounts: EnrichedBankAccountDetails[]
): PreferredBankAccount => {
  const fallback = savedBankAccounts.filter((account) => account.error === null).pop() ?? null
  // return fallback account if there is no preferred bank account
  if (preferredBankAccount === null) {
    return {
      account: fallback,
      switched: false, // since there was nothing to switch from
      valid: fallback?.error === null,
      reason: fallback?.error ?? null,
    }
  }
  // return the preferred bank account if it's no errors,
  if (preferredBankAccount.error === null) {
    return {
      account: preferredBankAccount,
      switched: false,
      valid: true,
      reason: null,
    }
  }
  // return the fallback account if the preferred bank account with any errors
  if (fallback !== null) {
    return {
      account: fallback,
      switched: true,
      valid: true,
      reason: preferredBankAccount.error,
    }
  }
  // return the the preferred account if no fallback account
  return {
    account: preferredBankAccount,
    switched: false,
    valid: false,
    reason: preferredBankAccount.error,
  }
}
